import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { setRtdbConnected } from '../../redux/Slices/deviceSlice';

import { onValue, ref } from "firebase/database";
import { db, authReady } from "../../config/firebase";

// Unified-presence rollout, part 2 (2026-10-06): this component used to be
// the primary device-status source (online/offline/standby/app_closed,
// derived from RTDB `presence` + the backend's apiOnline/apiPresenceStatus
// props). Mahmoud's requirement — ONE server-computed status every surface
// only DISPLAYS — moved that entire computation to the backend
// (device.connectivity.state, device.presence_status; see
// resolveDeviceOnline/resolveDeviceStatus in utils/helpers.js, which now
// read those fields directly with zero RTDB involvement). This component
// is now narrowed to the ONE remaining legitimate non-status RTDB use: the
// raw `connected` bit, needed only to decide whether the RTDB-pushed
// `current_campaign` name is fresh enough to trust over the REST-computed
// `current_campaign_name` fallback (content freshness, not device status —
// see Show.jsx's `isRtdbCampaignNameLive`, Grouping/DeviceCard.jsx,
// assignPopup/DeviceCard.jsx). Kept named `DeviceLastLoginListener` (not
// renamed) to avoid touching its ~8 mount sites across the app for a purely
// cosmetic change — this doc comment is the canonical description of what
// it does now.
//
// `apiOnline`/`apiPresenceStatus` props some callers still pass are
// deliberately unused here — see git history for the status logic that
// used to consume them.
const DeviceLastLoginListener = ({ path, serialName }) => {
    const dispatch = useDispatch();
    const lastConnectedRef = useRef(null);

    const updateRtdbConnected = useCallback((connected) => {
        if (lastConnectedRef.current !== connected) {
            lastConnectedRef.current = connected;
            dispatch(setRtdbConnected({ [serialName]: connected }));
        }
    }, [dispatch, serialName]);

    useEffect(() => {
        let cancelled = false;
        let unsubscribe = () => {};

        authReady.then(() => {
            if (cancelled) return;
            const fullPath = `${process.env.REACT_APP_APP_ENV}/${path}`;
            const query = ref(db, fullPath);

            unsubscribe = onValue(query, (snapshot) => {
                if (!snapshot.exists()) return;
                const { connected } = snapshot.val() ?? {};
                updateRtdbConnected(connected === true);
            });
        });

        return () => { cancelled = true; unsubscribe(); };
    }, [path, updateRtdbConnected]);

    return null;
};

export default React.memo(DeviceLastLoginListener);
